/**
 * @fileoverview CSATRenderer component for the Storyteller web application.
 * Displays a CSAT question with radio buttons.
 *
 * @example
 * // Basic usage
 * <CSATRenderer schema={schema} uischema={uischema} />
 */

import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsEnumProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../../Icon'
import css from './CSATRenderer.module.css'

/**
 * @function CSATRenderer
 * @description Component for rendering a CSAT question with radio buttons.
 * @param {ControlProps & VanillaRendererProps} props - The props for the CSATRenderer component.
 * @returns {JSX.Element} The CSATRenderer component.
 */
function CSATRenderer({
    schema,
    uischema,
    handleChange,
    errors,
    path,
}: ControlProps & VanillaRendererProps) {
    const hasError = errors.length > 0

    const { t } = useTranslation()
    const [selected, setSelected] = useState<string>('')

    return (
        <>
            <div className={css.choice_container}>
                <div className={`${css.item} ${css.normal}`}>
                    <div className={css.radio_container}>
                        {schema.oneOf.map(({ title, const: value }) => (
                            <button
                                className={`${css.button} ${
                                    selected === value ? css.selected : ''
                                }`}
                                key={'csat-key-' + title}
                                onClick={() => {
                                    setSelected(value)
                                    if (schema.type === 'number') {
                                        handleChange(path, Number(value))
                                    } else {
                                        handleChange(path, value)
                                    }
                                }}
                            >
                                <input
                                    id={'csat-checkbox-' + title}
                                    className={css.radio_input}
                                    type="radio"
                                    value={value}
                                    name={'csat-checkbox-' + title}
                                />
                                <label htmlFor={'csat-checkbox-' + title}>
                                    <Icon
                                        name={
                                            `satisfied${title}` as keyof typeof Novoic.SupportedIcons
                                        }
                                    />
                                </label>
                            </button>
                        ))}
                    </div>
                </div>
                <div className={css.label_container}>
                    <span className={css.low_label}>{t(uischema.options?.low)}</span>
                    <span className={css.high_label}>{t(uischema.options?.high)}</span>
                </div>
            </div>
            {hasError && <div className={css.errorMessage}>{errors}</div>}
        </>
    )
}

export const CSATTester = rankWith(3, uiTypeIs('CSAT'))

export default withVanillaControlProps(withJsonFormsEnumProps(CSATRenderer))
